if (!customElements.get("bundle-sale-form")) {
  customElements.define(
    "bundle-sale-form",
    class BundleSaleForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        if (!this.form) return;

        this.isloading = false; // Loading state

        // Cart drawer reference
        this.cartDrawer = document.getElementById("Cart-Drawer");

        // Add to cart button
        this.addCartButton = this.querySelector("button[type='submit']");

        this.boundOnSubmitHandler = this.onSubmitHandler.bind(this);
        this.boundOnChange = this.onChange.bind(this);
        this.form.addEventListener("submit", this.boundOnSubmitHandler); // Add-to-cart listener
        this.form.addEventListener("change", this.boundOnChange); // Product quantity change listener

        // Correct quantity selector boundaries and values
        this.setQuantityBoundaries();

        // Validate selection state
        this.validateSelection();

        // Initialize total price calculation
        this.summaryTotal();

        // Initialize quantity sync listeners
        if (!this.hasAttribute("data-show-quantity-selector"))
          this.bindQuantitySync();

        // Listen for cart update events and update quantity selectors
        // This handles updates from other locations, such as the cart or product page forms
        this.cartUpdateUnsubscriber = webvista.subscribe(
          PUB_SUB_EVENTS.cartUpdate,
          (event) => {
            if (event.source === "bundle-sale-form") return;

            // Check if the variant is one of the currently listed variants
            const variantIds = Array.from(
              this.querySelectorAll("input[name='id']"),
            ).map((input) => String(input.value)); // Use String to ensure type consistency
            if (!variantIds.includes(String(event.productVariantId))) return;

            // Update quantity selectors
            if (this.fetchController) this.fetchController.abort();
            this.fetchController = new AbortController();

            webvista
              .fetchHtml(
                `${window["routes"]["root_url"]}?section_id=${this.dataset.section}`,
                this.fetchController.signal,
              )
              .then((html) => {
                this.updateBundleQuantityRulers(html);
                this.setQuantityBoundaries();
                // Recalculate total since quantity selector values may have been reset
                this.summaryTotal();
              })
              .catch((error) => {
                // console.log(error);
              });
          },
        );
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }

        if (this.form && this.boundOnSubmitHandler) {
          this.form.removeEventListener("submit", this.boundOnSubmitHandler);
        }

        if (this.form && this.boundOnChange) {
          this.form.removeEventListener("change", this.boundOnChange);
        }

        if (this.fetchController) {
          this.fetchController.abort();
        }
      }

      /**
       * Handles change events and updates total price.
       */
      onChange(event) {
        this.validateSelection();

        // Update total price
        setTimeout(() => {
          this.summaryTotal();
        });
      }

      /**
       * Validates product selection state and updates submit button state.
       */
      validateSelection() {
        this.noValidSelection = !this.querySelector(
          "input[name='id']:checked:not(:disabled)",
        );
        this.setButtonState(this.noValidSelection);
      }

      /**
       * Calculates and displays the bundle total price.
       */
      summaryTotal() {
        const bundleProductList = Array.from(
          this.querySelectorAll(".bundle-product-card"),
        );

        const total = bundleProductList.reduce(
          (sum, item) => {
            if (!item.querySelector('input[name="id"]')?.checked) return sum;

            const price =
              parseFloat(
                item.querySelector(".card-price .price")?.dataset.value,
              ) || 0;

            const originalPrice =
              parseFloat(
                item.querySelector(".card-price .price")?.dataset.original,
              ) || price; // Fall back to current price when original is absent

            const quantity =
              parseInt(item.querySelector("input[name='quantity']")?.value) ||
              1;

            return {
              current: sum.current + price * quantity,
              original: sum.original + originalPrice * quantity,
            };
          },
          { current: 0, original: 0 },
        );

        const priceEl = this.querySelector(".bundle-total .price");
        if (priceEl) {
          priceEl.textContent = webvista.formatPriceAmount(
            total.current,
            Shopify.currency.active,
          );
        }

        const originalEl = this.querySelector(".bundle-total .original");
        if (originalEl) {
          if (total.original > total.current) {
            // Only show original price when it exceeds the current price
            originalEl.textContent = webvista.formatPriceAmount(
              total.original,
              Shopify.currency.active,
            );
          } else {
            // Otherwise hide it
            originalEl.textContent = "";
          }
        }
      }

      /**
       * Handles the add-to-cart form submission.
       */
      onSubmitHandler(event) {
        if (this.isloading) return;
        event.preventDefault();

        // Submit to cart
        const btn = event.submitter || this.addCartButton;
        this.addToCart(btn);
      }

      /**
       * Executes the submission to the cart
       * @param button Submit button
       */
      addToCart(button = null) {
        if (this.noValidSelection) return;

        this.startLoading();

        const config = webvista.fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";

        // All valid checked variant IDs
        const items = this.getSelectedItems();
        if (items.length <= 0) {
          this.setButtonState(); // Disable submit button
          this.endLoading();
          return;
        }

        config.body = JSON.stringify({
          items: items,
          sections: this.getSectionsToRender().map(
            (section) => section.section,
          ),
        });

        fetch(`${window["routes"]["cart_add_url"]}`, config)
          .then((response) => {
            return response.json();
          })
          .then((response) => {
            if (response.status) {
              webvista.popToast(response.description, "error");
              return (this.error = true);
            }

            this.error = false;

            // Update bundle product quantity rules
            const html = new DOMParser().parseFromString(
              response["sections"][this.dataset.section],
              "text/html",
            );
            this.updateBundleQuantityRulers(html);
            // Correct quantity input boundaries
            this.setQuantityBoundaries();
            // Recalculate total since quantity selector values may have been reset
            this.summaryTotal();

            // Filter out the current section — no need to replace its HTML
            const sectionsNeedRender = this.getSectionsToRender().filter(
              (section) => section.id !== this.id,
            );

            // Dynamically update section contents
            SectionDynamicUpdate.updateSections(
              sectionsNeedRender,
              response["sections"],
            );

            // Publish cart update events
            // Each variant change needs its own event
            items.forEach((item) => {
              webvista.publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "bundle-sale-form",
                productVariantId: item["id"],
              });
            });

            if (
              this.cartDrawer &&
              !this.cartDrawer.hasAttribute("data-status-silence")
            ) {
              // Open the cart drawer
              this.cartDrawer.show(button);
            } else {
              return (window.location = window["routes"]["cart_url"]);
            }
          })
          .catch((error) => {
            // console.log(error);
            webvista.popToast(
              window["accessibilityStrings"]["unknownError"],
              "error",
            );
            this.error = true;
          })
          .finally(() => {
            this.endLoading();
          });
      }

      /**
       * Get selected items
       * @param {string} parentId Parent ID (optional)
       * @returns {Array} Returns an array of selected items, each containing ID and quantity
       */
      getSelectedItems(parentId = null) {
        return Array.from(
          this.querySelectorAll("input[name='id']:checked:not(:disabled)"),
        ).map((input) => {
          const quantityInput = input
            .closest(".bundle-product-card")
            ?.querySelector("input[name='quantity']");
          return {
            id: input.value,
            quantity: parseInt(quantityInput?.value) || 1,
            ...(parentId && { parent_id: parentId }),
          };
        });
      }

      /**
       * Binds quantity input synchronization with display elements.
       */
      bindQuantitySync() {
        this.querySelectorAll("input[name='quantity']").forEach((input) => {
          input.addEventListener("change", (event) => {
            const value = event.target.value || 1;

            const displayElement = this.querySelector(
              `.quantity-count[data-product="${event.target.dataset.product}"]`,
            );
            if (displayElement) displayElement.textContent = value;
          });
        });
      }

      /**
       * Updates the quantity of items via external call
       * @param {number} value - The quantity to set
       */
      setItemsQuantity(value) {
        // Check if external quantity updates are allowed
        if (!this.hasAttribute("data-allow-external-quantity-update")) return;

        const quantity = value ?? 1;
        this.querySelectorAll("input[name='quantity']").forEach((input) => {
          input.value = quantity;

          // Manually trigger the change event
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      /**
       * Returns the sections that need to be re-rendered after cart update.
       * @returns {[{section: string, id: string},{section: string, selector: string, id: string}]}
       */
      getSectionsToRender() {
        const sections = [
          {
            id: this.id,
            section: this.dataset.section,
          },
          {
            id: "Cart-Icon-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
          {
            id: "Bottom-Cart-Bubble",
            section: "cart-icon-bubble",
            selector: ".shopify-section",
          },
          {
            id: "Cart-Drawer",
            section: "cart-drawer",
            selector: ".cart-count",
          },
        ];

        if (
          this.cartDrawer &&
          !this.cartDrawer.hasAttribute("data-status-silence")
        ) {
          sections.push({
            id: "Cart-Drawer",
            section: this.cartDrawer.dataset.section,
            selector: "#Cart-Drawer-Details",
          });
        }

        return sections;
      }

      /**
       * Updates bundle product quantity rules from fetched HTML.
       * @param html Parsed HTML document containing updated quantity data
       */
      updateBundleQuantityRulers(html) {
        const targetQuantityInputs = this.querySelectorAll(
          "input[name='quantity']",
        );
        targetQuantityInputs.forEach((targetInput) => {
          const sourceInput = html.getElementById(targetInput.id);

          if (sourceInput) {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];

            for (let attribute of attributes) {
              const sourceInputValue = sourceInput.getAttribute(attribute);
              if (sourceInputValue != null) {
                targetInput.setAttribute(attribute, sourceInputValue);
              }
            }
          }
        });
      }

      /**
       * Corrects quantity input boundaries for all quantity selectors.
       */
      setQuantityBoundaries() {
        this.querySelectorAll("quantity-input").forEach((quantityInput) => {
          quantityInput.setQuantityBoundaries();
        });
      }

      startLoading() {
        this.isloading = true;

        if (this.addCartButton) {
          this.addCartButton.setAttribute("disabled", "disabled");
          this.addCartButton.setAttribute("aria-busy", "true");
          this.addCartButton.classList.add("loading");
        }
      }

      endLoading() {
        this.isloading = false;

        if (this.addCartButton) {
          this.addCartButton.removeAttribute("disabled");
          this.addCartButton.setAttribute("aria-busy", "false");
          this.addCartButton.classList.remove("loading");
        }
      }

      /**
       * Sets the submit button enabled/disabled state.
       * @param disabled Whether to disable the button
       */
      setButtonState(disabled = true) {
        this.addCartButton?.toggleAttribute("disabled", disabled);
      }
    },
  );
}
